"use client"

import { ClerkFailed, useAuth } from "@clerk/nextjs"
import type { ReactNode } from "react"
import { useEffect } from "react"

const ERROR_RELOAD_KEY = "maatii_clerk_err_reload_ts"
const STUCK_RELOAD_KEY = "maatii_clerk_stuck_reload_v1"
const STUCK_MS = 26_000
const ERROR_RELOAD_COOLDOWN_MS = 45_000

/**
 * When Clerk reaches `error` (e.g. Frontend API network failure), retry once after a short delay.
 * Cooldown prevents reload loops if the outage persists.
 */
function ClerkErrorAutoRetry() {
  useEffect(() => {
    const now = Date.now()
    let last = 0
    try {
      last = Number(sessionStorage.getItem(ERROR_RELOAD_KEY) || 0)
    } catch {
      return
    }
    if (now - last < ERROR_RELOAD_COOLDOWN_MS) return
    try {
      sessionStorage.setItem(ERROR_RELOAD_KEY, String(now))
    } catch {
      return
    }
    const id = window.setTimeout(() => window.location.reload(), 600)
    return () => window.clearTimeout(id)
  }, [])
  return null
}

/**
 * If Clerk never finishes initializing (stuck loading), one guarded full reload can recover
 * from transient TLS/DNS issues without leaving the tab permanently blank. Runs in development
 * and production; `sessionStorage` ensures at most one automatic reload per tab session.
 */
function ClerkStuckInitRecovery() {
  const { isLoaded } = useAuth()

  useEffect(() => {
    if (isLoaded) return

    const id = window.setTimeout(() => {
      try {
        if (sessionStorage.getItem(STUCK_RELOAD_KEY)) return
        sessionStorage.setItem(STUCK_RELOAD_KEY, "1")
      } catch {
        return
      }
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console -- intentional dev-only operator signal
        console.warn(
          "[MAATII] Clerk did not reach `isLoaded` within",
          STUCK_MS,
          "ms — reloading once (stuck-init recovery). If this repeats, check DevTools → Network for blocked or pending requests to Clerk or /__clerk.",
        )
      }
      window.location.reload()
    }, STUCK_MS)

    return () => window.clearTimeout(id)
  }, [isLoaded])

  return null
}

export function ClerkProductionResilience({ children }: { children: ReactNode }) {
  return (
    <>
      <ClerkFailed>
        <ClerkErrorAutoRetry />
      </ClerkFailed>
      <ClerkStuckInitRecovery />
      {children}
    </>
  )
}
